import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageLayout from "../components/layout/PageLayout";
import { HERO_IMAGES } from "../lib/constants";
import { BookOpen, Play, Award, Users } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const COURSES = [
    { t: "Understanding NIL", d: "The full landscape — rules, compliance, and how to profit responsibly.", len: "6 lessons · 1h 20m" },
    { t: "Building a Personal Brand", d: "From identity to visual style to voice — build a brand that lasts.", len: "8 lessons · 2h 10m" },
    { t: "Social Media Responsibility", d: "Post smart. Protect the athlete. Grow the audience.", len: "5 lessons · 55m" },
    { t: "Media Training", d: "Interviews, cameras and pressers — own every mic drop.", len: "6 lessons · 1h 15m" },
    { t: "Financial Literacy", d: "Money in, money out — for teenage and young-adult athletes.", len: "10 lessons · 2h 45m" },
    { t: "Contract Basics", d: "Read a deal. Spot a red flag. Ask the right questions.", len: "7 lessons · 1h 40m" },
    { t: "Taxes for Athletes", d: "1099s, W-9s, state tax and quarterly filings, demystified.", len: "5 lessons · 1h 10m" },
    { t: "Sponsorship Etiquette", d: "How to work with brands so they invite you back.", len: "4 lessons · 45m" },
    { t: "Public Speaking", d: "Own the stage — camps, banquets, boardrooms.", len: "6 lessons · 1h 30m" },
    { t: "Crisis Management", d: "When things go sideways — how to respond, not react.", len: "3 lessons · 40m" },
    { t: "Content Creation", d: "Shoot, cut, caption. From phone-shot to portfolio-quality.", len: "9 lessons · 2h 40m" },
    { t: "Life After Sports", d: "Business, real estate, coaching, entrepreneurship.", len: "8 lessons · 2h" },
];

export default function Academy() {
    const [pkgs, setPkgs] = useState([]);
    const [loading, setLoading] = useState(null);

    useEffect(() => { api.get("/payments/packages").then(r => setPkgs(r.data)).catch(() => {}); }, []);

    const buy = async (id) => {
        setLoading(id);
        try {
            const { data } = await api.post("/payments/checkout", { package_id: id, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(formatApiError(e));
            setLoading(null);
        }
    };

    const academyPkgs = pkgs.filter(p => p.id.startsWith("academy_"));

    return (
        <PageLayout>
            <section className="relative min-h-[50vh] flex items-end pb-16 pt-32 border-b border-white/10 overflow-hidden">
                <img src={HERO_IMAGES.huddle} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Digital Beast Academy</div>
                    <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-tight" data-testid="academy-heading">
                        LEARN THE GAME<br/><span className="text-[#d4af37]">BEHIND THE GAME.</span>
                    </h1>
                    <p className="text-white/70 max-w-2xl mt-6 text-lg">
                        Video lessons, quizzes and certificates for athletes and parents — built by professionals who work with athletes every day.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    {academyPkgs.length > 0 && (
                        <>
                            <div className="section-label mb-5">Enrollment</div>
                            <div className="grid md:grid-cols-2 gap-4 mb-16" data-testid="academy-packages">
                                {academyPkgs.map(p => (
                                    <div key={p.id} className="surface p-8 beam-card">
                                        <div className="section-label mb-3">Course</div>
                                        <div className="font-display text-3xl text-white leading-tight">{p.name}</div>
                                        <div className="mt-6 flex items-baseline gap-2">
                                            <div className="font-display text-5xl text-[#d4af37]">${p.amount}</div>
                                            <div className="text-white/40 text-sm">USD one-time</div>
                                        </div>
                                        <button onClick={() => buy(p.id)} disabled={loading === p.id} className="btn-gold w-full justify-center mt-8" data-testid={`academy-buy-${p.id}`}>
                                            {loading === p.id ? "Redirecting…" : "Enroll Now"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <div className="section-label mb-5">Curriculum</div>
                    <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight mb-10">12 Core Courses</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {COURSES.map((c, i) => (
                            <div key={c.t} className="surface surface-hover p-6" data-testid={`course-${i}`}>
                                <div className="flex items-start justify-between">
                                    <BookOpen size={22} className="text-[#d4af37]" strokeWidth={1.5} />
                                    <span className="text-white/40 text-xs font-mono">{String(i+1).padStart(2, "0")}</span>
                                </div>
                                <div className="font-display text-xl text-white mt-5">{c.t}</div>
                                <p className="text-white/55 text-sm mt-2 leading-relaxed">{c.d}</p>
                                <div className="text-white/40 text-xs mt-4 uppercase tracking-widest">{c.len}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
